import { useCallback, useEffect, useState } from "react";

import { useGetListQuery } from "@/pages/Private/redux/list/list.api";
import {
	BulkFindPersonSelection,
	usePlanBulkFindPersonMutation,
} from "@/pages/Private/redux/list/list-find-person.api";
import {
	findPeopleListSelector,
	findPeoplePlanSelector,
	FindPeoplePlanState,
	forgetFindPeoplePlan,
} from "@/pages/Private/redux/list/list-find-person.slice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { List } from "../../schema/list";

/**
 * The plan of one opening of the bulk Find people dialog, from the slice it is filed in under the
 * opening's key (apps/web §2): asked for when the dialog opens, asked again on `replan` (Try
 * again, or a search the server refused because the plan changed), and forgotten when the dialog
 * closes. A failed plan reads as an error, never as a plan of nothing (PREC-4398).
 */
export function useFindPeoplePlan(
	sessionKey: string,
	selection: BulkFindPersonSelection
): { plan: FindPeoplePlanState; replan: () => void } {
	const dispatch = useAppDispatch();
	const [planBulkFindPerson] = usePlanBulkFindPersonMutation();
	const plan = useAppSelector(findPeoplePlanSelector(sessionKey));

	const replan = useCallback(() => {
		// The slice records the failure; nothing else waits for it.
		planBulkFindPerson({ sessionKey, selection })
			.unwrap()
			.catch(() => undefined);
	}, [planBulkFindPerson, selection, sessionKey]);

	useEffect(() => {
		replan();

		return () => {
			dispatch(forgetFindPeoplePlan(sessionKey));
		};
	}, [dispatch, replan, sessionKey]);

	return { plan, replan };
}

/**
 * The list the dialog was opened on, read again on open so the dialog starts from its current
 * settings, from the slice's bucket for that list. The dialog seeds once from that read; a later
 * background refetch does not unmount it (apps/web §24b).
 */
export function useFindPeopleList(listId: number): { list?: List; failed: boolean } {
	const { isError, isFetching } = useGetListQuery(listId, { refetchOnMountOrArgChange: true });
	const list = useAppSelector(findPeopleListSelector(listId));
	const [loaded, setLoaded] = useState(false);
	const fresh = !!list && !isFetching && !isError;

	if (fresh && !loaded) {
		setLoaded(true);
	}

	return { list: loaded || fresh ? list : undefined, failed: isError && !loaded };
}
